import { useCallback, useState } from "react";
import { downloadTransactionReceipt } from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../../components/SalesReportExport/utils";

export const PDF_DOWNLOAD_ERROR_MESSAGE =
  "ไม่สามารถดาวน์โหลด PDF ได้ กรุณาลองใหม่อีกครั้ง";

export const PDF_VIEW_ERROR_MESSAGE =
  "ไม่สามารถเปิดไฟล์ PDF ได้ กรุณาลองใหม่อีกครั้ง";

/**
 * Did the axios interceptor already put a message on screen for this failure?
 *
 * `pushGlobalError` is a single slot, not a queue: whatever is pushed last is
 * what the user reads. So when the request came back with an HTTP status, the
 * interceptor has already resolved the right message for it — 404 "ไม่พบไฟล์
 * ใบเสร็จ...", 502 "สร้างไฟล์ใบเสร็จไม่สำเร็จ..." — and pushing the generic
 * one here would bury it. That is exactly what used to happen: the 404 text
 * configured on the request never reached the list pages at all.
 *
 * Anything without a response — a dropped connection, a cancelled request, or
 * a 200 carrying a body that is not a usable PDF — has no message but ours.
 *
 * Tested structurally rather than with `axios.isAxiosError`: importing axios
 * here would pull its ESM build into every test that renders this hook, and
 * CRA's jest does not transform it. Carrying an HTTP status is the property
 * that actually matters anyway.
 */
function handledByInterceptor(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null | undefined)
    ?.response?.status;
  return typeof status === "number";
}

/**
 * Shared handler for downloading a transaction's receipt PDF from the existing
 * report download endpoint (`downloadTransactionReceipt`). Used by both the
 * Transaction list and the Sale Daily Report item list.
 *
 * - Tracks the currently downloading id so a single row can show a loading
 *   state and concurrent/duplicate clicks are ignored.
 * - Shows a warning toast on failure, but only for the failures nothing else
 *   describes: a request that came back with a status is left to the message
 *   the interceptor already chose for it (see `handledByInterceptor`).
 */
export function usePdfDownload() {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const downloadPdf = useCallback(
    async (uuid: string | null | undefined) => {
      // Ignore invalid ids and prevent duplicate clicks while a download runs.
      if (!uuid || downloadingId) return;

      setDownloadingId(uuid);
      try {
        const response = await downloadTransactionReceipt(uuid);
        const blob = response.data as Blob;

        // Guard against an invalid file response (empty body or JSON error
        // payload returned with a success status).
        if (
          !(blob instanceof Blob) ||
          blob.size === 0 ||
          blob.type.includes("application/json")
        ) {
          throw new Error("Invalid PDF file response");
        }

        const filename =
          getFilenameFromContentDisposition(
            response.headers["content-disposition"] as string | undefined,
          ) || `receipt-${uuid}.pdf`;

        downloadBlob(filename, blob);
      } catch (error) {
        if (!handledByInterceptor(error))
          pushGlobalError(PDF_DOWNLOAD_ERROR_MESSAGE);
      } finally {
        setDownloadingId(null);
      }
    },
    [downloadingId],
  );

  const [viewingId, setViewingId] = useState<string | null>(null);

  // Open a transaction's receipt PDF inline in a new browser tab. The endpoint is
  // authenticated (Bearer via the axios interceptor), so we fetch the blob first
  // and hand it to the tab as an object URL — we can't just window.open() the API
  // URL. The blank tab is opened synchronously inside the click gesture so popup
  // blockers don't kill it; its location is set once the blob arrives.
  const openPdf = useCallback(
    async (uuid: string | null | undefined) => {
      if (!uuid || viewingId) return;

      const tab = window.open("", "_blank");
      setViewingId(uuid);
      try {
        const response = await downloadTransactionReceipt(uuid);
        const raw = response.data as Blob;

        if (
          !(raw instanceof Blob) ||
          raw.size === 0 ||
          raw.type.includes("application/json")
        ) {
          throw new Error("Invalid PDF file response");
        }

        // Force the application/pdf type so the browser renders it inline
        // instead of downloading it.
        const blob = new Blob([raw], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        if (tab) {
          tab.location.href = url;
        } else {
          // Popup was blocked — fall back to a same-tab navigation.
          window.open(url, "_blank", "noopener");
        }
        // Revoke after the tab has had time to load the document.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (error) {
        if (tab) tab.close();
        if (!handledByInterceptor(error)) pushGlobalError(PDF_VIEW_ERROR_MESSAGE);
      } finally {
        setViewingId(null);
      }
    },
    [viewingId],
  );

  return { downloadingId, downloadPdf, viewingId, openPdf };
}
